import { Injectable }       from '@angular/core';

import { DropdownQuestion } from '../../question-dropdown';
import { QuestionBase, QuestionVisiblityOperation, QuestionVisiblityOperators, QuestionBaseParams, QuestionUITypes, QuestionRequestTypes, QuestionElement }     from '../../question-base';
import { TextboxQuestion }  from '../../question-textbox';
import { DropdownQuestionBoolean } from '../../question-dropdown-boolean';
import { IntegerboxQuestion }  from '../../question-integerbox';
import { DecimalboxQuestion }  from '../../question-decimalbox';
import { QuestionRoot } from '../../question-root';
import { QuestionPage } from '../../question-page';
import { QuestionRow } from '../../question-row';
import { ButtonQuestion }  from '../../question-button';
import { QuestionCol2 }  from '../../question-col2';
import { ModalQuestion, ModalButtonQuestion }  from '../../question-modal';

import { QuestionJsonService, QuestionJsonElement, Items } from '../question-json/question-json.service';
import { FormValuesService } from '../form-values/form-values.service';


export interface DynamicQuestions { [key: string]: QuestionBase<any> };

@Injectable()
export class QuestionService {
  
  private dynamicQuestionsByPage: Promise<any>;
  
  constructor(){}
  
  public questionsBuilder( formData : Array<any> ) : QuestionRoot {

    let questionRoot : QuestionRoot = new QuestionRoot();
  
    formData.forEach(( formPage ) => {
      let questionPageCurrent = new QuestionPage( formPage.id, formPage.displayText );
      questionRoot.setPage( formPage.id, questionPageCurrent );
      this.recursiveQuestionBuilder( <any> questionPageCurrent, formPage.items );
    });
    return questionRoot;
  }

  private recursiveQuestionBuilder( questionFormat : QuestionRow | QuestionCol2, items){

    items.forEach(elem => {      
      if ( elem.uiType === "row" ) {
        let questionRowNew : QuestionElement = <QuestionElement> new QuestionRow( elem.rowId );
        this.recursiveQuestionBuilder( questionRowNew, elem.rowitems );
        questionFormat.addQuestion( questionRowNew );
        
      } else if (elem.uiType === "col2" ) {
        let questionColNew : QuestionElement = <QuestionElement> new QuestionCol2( elem.colId );
        questionColNew.label = elem.displayText;
        this.recursiveQuestionBuilder( questionColNew, elem.colitems );
        questionFormat.addQuestion( questionColNew );

      } else if ( elem.uiType === "modal" ) {
        let questionModalNew : QuestionElement = <QuestionElement> new ModalQuestion( elem.modalid );
        questionModalNew.modalId = elem.modalId;
        this.recursiveQuestionBuilder( questionModalNew, elem.modalitems );
        questionFormat.addQuestion( questionModalNew );
        
      } else {
        questionFormat.addQuestion( this.questionMainBuilder(elem) );
      }
    });
  }

  private getQuestionVisiblityValue( questionVisiblityValueString : string ) : boolean | number | string{
    if( questionVisiblityValueString === 'true' ){
      return true;
    }

    if( questionVisiblityValueString === 'false' ){
      return true;
    }

    if( isFinite( <any>questionVisiblityValueString ) ){
      return parseInt( questionVisiblityValueString );
    }

    return questionVisiblityValueString;
  }

  private getQuestionVisiblityOperators( element : Items ) : Array<QuestionVisiblityOperation>{
    let res : Array<QuestionVisiblityOperation> = [];

    if(element.visibleControl && element.visibleIfOperator && element.visibleIfValue){
      let visibilityKeys : Array<string> = element.visibleControl.split('|');
      let visibilityOperators : Array<number> = [];
      let visibilityValues : Array<any> = [];
      let tmp: Array<string>;
      let i;

      tmp = element.visibleIfOperator.split('|');

      tmp.forEach((val) => {
        visibilityOperators.push(<QuestionVisiblityOperators> parseInt( val ));
      });

      tmp = element.visibleIfValue.split('|');

      tmp.forEach((val) => {
        visibilityValues.push( val );
      });

      if( visibilityKeys.length != visibilityOperators.length || visibilityKeys.length != visibilityValues.length ){
        console.error( 'Didnt think of that' );
      }      

      for( i = 0; i < visibilityKeys.length; i++ ){
        res.push({
          key : visibilityKeys[i],
          operator : visibilityOperators[i],
          value : visibilityValues[i],
        });
      }
    }

    return res;
  }
  
  private readonly keyPrefix = 'pre';
  private keyIndex = 0;
  
  private questionMainBuilder(element) {
    let question;
    let keyCryptic = this.keyPrefix + this.keyIndex++;
    let value = element.sampleValue;
    let visiblityOperations = this.getQuestionVisiblityOperators( element );
    let questionType: QuestionUITypes = QuestionBase.questionUITypeFromString( element.uiType );
    let requestType: QuestionRequestTypes = QuestionBase.requestTypeFromString( element.requestType );

    let params : QuestionBaseParams<string> = {
      key:                    element.jsonName,
      label:                  element.displayText,
      standardValue:          element.sampleValue,
      visibilityOperations:   visiblityOperations,
      requestType:            requestType,
      displayTextPosition:    element.displayTextPosition,
      divider:                element.divider,
      keyCryptic:             keyCryptic   
    };

    if( questionType === QuestionUITypes.ERROR || requestType === QuestionRequestTypes.ERROR ){
      console.error( 'Question or questionRequestType parsing went wrong' );
    }

    switch ( questionType ) {
      case QuestionUITypes.STRING:
        question = new TextboxQuestion({
          ...params,
          required: !element.optional
        });
      break;
        
      case QuestionUITypes.INTEGER:
        question = new IntegerboxQuestion({
          ...params,
          standardValue:    this.decimalSeparatorCheck(element.sampleValue),
          required:         !element.optional,
        });
      break;  
        
      case QuestionUITypes.DECIMAL:
        question = new DecimalboxQuestion({
          ...params,
          standardValue:    this.decimalSeparatorCheck(element.sampleValue),
          required:         !element.optional
        });
      break;  
      
      case QuestionUITypes.DROPDOWN:
        let selectOptions = [];
        
        element.selectValuesDisplayText.forEach( (text,index) => {
          selectOptions.push( { value: element.selectValues[index] , label: text  } );
        });

        question = new DropdownQuestion({
          ...params,
          options: selectOptions
        });
      break;
        
      case QuestionUITypes.BOOLEAN:
        if( element.sampleValue !== 'true' && element.sampleValue !== 'false' ){
          console.log( 'Boolean api value is bad' );
        }

        question = new DropdownQuestionBoolean({
          ...params,
          standardValue: element.sampleValue
        });
      break;
      
      case QuestionUITypes.BUTTON:
        question = new ButtonQuestion({
          ...params,
          required: !element.optional,
        });
      break;      
      
      case QuestionUITypes.MODALBUTTON:
        question = new ModalButtonQuestion({
          ...params,
          required: !element.optional,
          executeId: element.executeId
        });
        
    break;   
    }

    return question;
  }
  
  decimalSeparatorCheck(ele){
    try { 
      let xD = ele.replace(",", ".") 
      return xD
    } catch (e) { 
      return ele 
    }
  }  
  
}


// WEBPACK FOOTER //
// ./src/app/services/question/question.service.ts